import type { MetadataRoute } from 'next'
import {
  DEFAULT_LOCALE,
  ENABLED_LOCALES,
  type Locale,
  toLanguageTag,
} from '@/lib/i18n/config'
import { localizePath, resolveLocaleRoute } from '@/lib/i18n/routing'
import { BREW_DEMO_BOOKING_URL } from '@/lib/site/booking'
import { POSITIONING } from '@/lib/site/messaging'
import { hasPublicMarkdownTwin } from '@/lib/site/seo/markdown-paths'
import type { SeoTranslator } from '@/lib/site/seo/seo-translator'

/** When unset, use localhost in `next dev` so metadataBase and canonical URLs match the dev server. */
const DEFAULT_SITE_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3000'
    : 'https://brew.new'

/**
 * Ensure URL has a protocol prefix
 */
function ensureProtocol(url: string): string {
  if (!url) {
    return DEFAULT_SITE_URL
  }
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  return `https://${url}`
}

export const siteConfig = {
  name: 'Brew',
  description: POSITIONING.tagline,
  cta: 'Get Started',
  url: ensureProtocol(process.env.NEXT_PUBLIC_APP_URL || DEFAULT_SITE_URL),

  // =============================================================================
  // SEO CONFIGURATION - Centralized metadata for all pages
  // =============================================================================
  // Copy lives in the `seo` catalog (`messages/<locale>/seo.json`); the
  // marketing tree reads it through `lib/site/seo/page-metadata.ts`. The
  // English strings below serve callers without a locale: the product root
  // layouts (`getBaseMetadata()`). `tests/seo-i18n.test.ts` holds every one
  // equal to the catalog.
  seo: {
    title: {
      default: POSITIONING.homeTitle,
      template: '%s | Brew',
    },
    description: POSITIONING.shortMeta,

    // Where each `seo.pages.<key>` page lives. `absoluteTitle` opts out of
    // the '%s | Brew' template (title rendered verbatim).
    pages: {
      home: {
        path: '/',
        absoluteTitle: true,
        // og:video: the og:image layout (1A) as a silent 12.6s loop, its
        // email pair cycling through template-gallery emails; 1200×630 to
        // match. Unfurlers that ignore og:video fall back to the image. New
        // cuts get a new CDN key, never an overwrite: shared links cache it.
        ogVideo: {
          url: 'https://cdn.brew.new/assets/2026/10/brew-home-og-29e9c56b.mp4',
          type: 'video/mp4',
          width: 1200,
          height: 630,
        },
      },
      pricing: {
        path: '/pricing',
      },
      enterprise: {
        path: '/enterprise',
      },
      mcp: {
        path: '/mcp',
      },
      marketers: {
        path: '/marketers',
      },
      startups: {
        path: '/startups',
      },
      agencies: {
        path: '/agencies',
      },
      ecommerce: {
        path: '/ecommerce',
      },
      developers: {
        path: '/developers',
        absoluteTitle: true,
      },
      emailAudit: {
        path: '/email-audit',
      },
      university: {
        path: '/university',
        absoluteTitle: true,
      },
      support: {
        path: '/support',
      },
      blog: {
        path: '/blog',
      },
      integrations: {
        path: '/browse/integrations',
      },
      customers: {
        path: '/customers',
      },
    },

    // OpenGraph defaults
    openGraph: {
      type: 'website' as const,
      siteName: 'Brew',
      images: {
        default: {
          url: '/opengraph-image',
          width: 1200,
          height: 630,
          alt: 'Brew: AI-Native ESP for Modern Teams and AI Agents',
        },
      },
    },

    // og:video per integration page, keyed by provider id: the page's 3B
    // og:image animated (frame 0 is the still; a sky panel rises with
    // emails, then settles back), 1200×630. New cuts get a new CDN key.
    integrationOgVideos: {
      figma: {
        url: 'https://cdn.brew.new/assets/2026/10/figma-og-00ee6c95.mp4',
        type: 'video/mp4',
        width: 1200,
        height: 630,
      },
      stripe: {
        url: 'https://cdn.brew.new/assets/2026/10/stripe-og-0a0ba0c4.mp4',
        type: 'video/mp4',
        width: 1200,
        height: 630,
      },
    },

    // Twitter defaults
    twitter: {
      card: 'summary_large_image' as const,
      creator: '@brewdotnew',
      site: '@brewdotnew',
    },
  },

  // =============================================================================
  // CONTACT INFORMATION - Centralized contact details
  // =============================================================================
  contact: {
    email: {
      support: 'support@brew.new',
      founders: 'founders@brew.new',
      sales: 'founders@brew.new',
    },
    phone: '+1-(332)-203-2145',
    scheduling: BREW_DEMO_BOOKING_URL,
  },

  // =============================================================================
  // SOCIAL LINKS - Consolidated social media links
  // =============================================================================
  social: {
    twitter: 'https://x.com/brewdotnew',
    twitterHandle: '@brewdotnew',
    youtube: 'https://www.youtube.com/@brewdotnew',
    linkedin: 'https://www.linkedin.com/company/brewdotnew',
  },

  // =============================================================================
  // JSON-LD SCHEMA TEMPLATES - Structured data for search engines
  // =============================================================================
  jsonLd: {
    organization: {
      '@type': 'Organization' as const,
      '@id': `${DEFAULT_SITE_URL}/#organization`,
      name: 'Brew',
      url: DEFAULT_SITE_URL,
      // A raster logo: Google wants at least 112×112 px, not an SVG favicon.
      logo: `${DEFAULT_SITE_URL}/logo/brew-logo-512.png`,
      sameAs: [
        'https://x.com/brewdotnew',
        'https://www.linkedin.com/company/brewdotnew',
        'https://www.youtube.com/@brewdotnew',
        'https://github.com/GetBrew',
      ],
    },
    software: {
      '@type': 'SoftwareApplication' as const,
      name: 'Brew',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer' as const,
        price: '0',
        priceCurrency: 'USD',
      },
    },
    website: {
      '@type': 'WebSite' as const,
      '@id': `${DEFAULT_SITE_URL}/#website`,
      url: DEFAULT_SITE_URL,
      name: 'Brew',
    },
  },
}
// =============================================================================
// HELPER FUNCTIONS - Utilities for generating metadata from config
// =============================================================================

export function getSiteUrl(): string {
  return siteConfig.url.replace(/\/$/, '')
}

export function getAbsoluteUrl(path = ''): string {
  const siteUrl = getSiteUrl()

  if (!path) {
    return siteUrl
  }

  return `${siteUrl}${path.startsWith('/') ? path : `/${path}`}`
}

// =============================================================================
// LANGUAGE VERSIONS - One page, one URL per enabled locale
// =============================================================================

/**
 * More than one locale is served. Only then do pages carry hreflang links,
 * `og:locale:alternate` and structured-data `inLanguage`: with English
 * alone, every head stays as it was before the localized tree.
 */
export function isMultilingual(): boolean {
  return ENABLED_LOCALES.length > 1
}

/** `path` (a path without locale) in `locale`, as an absolute URL. */
export function getLocalizedUrl(locale: Locale, path: string): string {
  return getAbsoluteUrl(localizePath(locale, path))
}

/**
 * `path` in `locale` the way structured data and the root Open Graph tags
 * name it: like `getLocalizedUrl`, except that the home page is the bare
 * site URL (`https://brew.new`, `https://brew.new/de`).
 */
export function getLocalizedNodeUrl(locale: Locale, path: string): string {
  const localized = localizePath(locale, path)
  return getAbsoluteUrl(localized === '/' ? '' : localized)
}

/**
 * User-made templates and flows (`/explore`, the galleries, their detail
 * pages), and the free email audit until its tool is translated, are
 * indexed in English only: in every other locale they are
 * `noindex` and belong to no hreflang cluster or sitemap.
 */
const ENGLISH_ONLY_PATH =
  /^\/(?:explore|browse\/templates|browse\/flows)(?:\/|$)/

/** True for a page indexed only in English (`ENGLISH_ONLY_PATH`). */
export function isEnglishOnlyPath(path: string): boolean {
  return ENGLISH_ONLY_PATH.test(path)
}

/** Pages no search engine indexes, in any language. */
const UNINDEXED_PATH =
  /^\/(?:sign-in|sign-up|create-organization|suspended)(?:\/|$)/

/**
 * The page at `path` (a path without locale) is served, and indexed, in
 * every enabled locale. Never while English is the only locale; never for
 * product pages (one URL for all), the English-only galleries or the auth
 * pages.
 */
function hasLanguageVersions(path: string): boolean {
  return (
    isMultilingual() &&
    resolveLocaleRoute(path).area === 'marketing' &&
    !ENGLISH_ONLY_PATH.test(path) &&
    !UNINDEXED_PATH.test(path)
  )
}

/** hreflang → URL: `alternates.languages`, in page metadata and the sitemap. */
export type LanguageAlternates = NonNullable<
  NonNullable<MetadataRoute.Sitemap[number]['alternates']>['languages']
>

/**
 * The hreflang cluster of the page at `path` (a path without locale): every
 * enabled locale's URL plus `x-default` → English. Page metadata and the
 * sitemap both build it here, so the two can never disagree. `undefined`
 * for a page without language versions (`hasLanguageVersions`).
 */
export function getLanguageAlternates(
  path: string
): LanguageAlternates | undefined {
  if (!hasLanguageVersions(path)) {
    return undefined
  }
  const languages: LanguageAlternates = {}
  for (const locale of ENABLED_LOCALES) {
    languages[toLanguageTag(locale) as keyof LanguageAlternates] =
      getLocalizedUrl(locale, path)
  }
  languages['x-default'] = getLocalizedUrl(DEFAULT_LOCALE, path)
  return languages
}

const OPEN_GRAPH_LOCALES: Readonly<Record<Locale, string>> = {
  en: 'en_US',
  de: 'de_DE',
  fr: 'fr_FR',
  es: 'es_ES',
  'pt-br': 'pt_BR',
  it: 'it_IT',
  nl: 'nl_NL',
  pl: 'pl_PL',
  sv: 'sv_SE',
  cs: 'cs_CZ',
  tr: 'tr_TR',
  ja: 'ja_JP',
  ko: 'ko_KR',
  th: 'th_TH',
  vi: 'vi_VN',
  id: 'id_ID',
}

/** `og:locale` for a locale (`en_US`, `pt_BR`). */
export function toOpenGraphLocale(locale: Locale): string {
  return OPEN_GRAPH_LOCALES[locale]
}

/**
 * `openGraph.locale` for the page at `path` in `locale`, and
 * `openGraph.alternateLocale` (the other enabled locales) when the page
 * has language versions (`hasLanguageVersions`).
 */
export function getOpenGraphLocale(
  locale: Locale,
  path: string
): { locale: string; alternateLocale?: Array<string> } {
  if (!hasLanguageVersions(path)) {
    return { locale: toOpenGraphLocale(locale) }
  }
  return {
    locale: toOpenGraphLocale(locale),
    alternateLocale: ENABLED_LOCALES.filter((other) => other !== locale).map(
      toOpenGraphLocale
    ),
  }
}

/**
 * The language of a page's structured data: its BCP 47 tag, with English
 * as the `en-US` the blog's graph has always carried.
 */
export function toContentLanguage(locale: Locale): string {
  return locale === DEFAULT_LOCALE ? 'en-US' : toLanguageTag(locale)
}

/** A page node's `inLanguage`, once more than one locale is served. */
export function getInLanguage(locale: Locale): { inLanguage?: string } {
  return isMultilingual() ? { inLanguage: toContentLanguage(locale) } : {}
}

/** `noindex, follow` for an English-only page in any other locale. */
export function getEnglishOnlyRobots(locale: Locale): {
  robots?: { index: false; follow: true }
} {
  return locale === DEFAULT_LOCALE
    ? {}
    : { robots: { index: false, follow: true } }
}

/**
 * Absolute `text/markdown` twin for a public path in `locale` (`/` →
 * `/index.md`, `/de/index.md`).
 */
export function getMarkdownAlternateHref(path: string, locale: Locale): string {
  const normalized =
    path === '/' || path === '' ? '/index' : path.replace(/\/$/, '')
  return `${getSiteUrl()}${localizePath(locale, normalized)}.md`
}

/**
 * The `alternates` of the page at `path` in `locale`, ready to spread into
 * metadata: its own URL as the canonical, its language versions
 * (`getLanguageAlternates`), and the `text/markdown` twin ONLY for paths
 * that actually have one (`hasPublicMarkdownTwin`, the same leaf inventory
 * the serving side uses), so a new page adopting this helper can never
 * advertise a 404.
 */
export function getCanonicalAlternates(path: string, locale: Locale) {
  const languages = getLanguageAlternates(path)
  return {
    alternates: {
      canonical: getLocalizedUrl(locale, path),
      ...(languages ? { languages } : {}),
      ...(hasPublicMarkdownTwin(path)
        ? {
            types: {
              'text/markdown': getMarkdownAlternateHref(path, locale),
            },
          }
        : {}),
    },
  }
}

function getOrganizationId(): string {
  return `${getSiteUrl()}/#organization`
}

function getWebsiteId(): string {
  return `${getSiteUrl()}/#website`
}

/** Footer legal name — the Delaware corporation, not a street address. */
const ORGANIZATION_LEGAL_NAME = 'Brew Emails Inc.'

/**
 * Shared Organization node for standalone JSON-LD and the homepage graph.
 * `address` is a country-only PostalAddress: no verified street HQ exists
 * in this repo. Do not invent `streetAddress`.
 */
export function getOrganizationNode() {
  return {
    ...siteConfig.jsonLd.organization,
    '@id': getOrganizationId(),
    url: getSiteUrl(),
    logo: getAbsoluteUrl('/logo/brew-logo-512.png'),
    legalName: ORGANIZATION_LEGAL_NAME,
    contactPoint: {
      '@type': 'ContactPoint' as const,
      telephone: siteConfig.contact.phone,
      email: siteConfig.contact.email.support,
      contactType: 'customer support',
    },
    address: {
      '@type': 'PostalAddress' as const,
      addressCountry: 'US',
    },
  }
}

/** A root layout's site-wide copy: the default title, description and share-image alt. */
export type BaseMetadataCopy = {
  title: string
  description: string
  imageAlt: string
}

const ENGLISH_BASE_METADATA_COPY: BaseMetadataCopy = {
  title: siteConfig.seo.title.default,
  description: siteConfig.seo.description,
  imageAlt: siteConfig.seo.openGraph.images.default.alt,
}

/**
 * Generate base metadata for a root layout. The product roots take the
 * English default; the marketing root passes its locale and the `seo`
 * catalog's copy (`getMarketingBaseMetadata`).
 */
export function getBaseMetadata(
  locale: Locale = DEFAULT_LOCALE,
  copy: BaseMetadataCopy = ENGLISH_BASE_METADATA_COPY
) {
  const { seo, name } = siteConfig
  const siteUrl = getSiteUrl()

  return {
    metadataBase: new URL(siteUrl),
    title: {
      default: copy.title,
      template: seo.title.template,
    },
    description: copy.description,
    authors: [{ name: 'Brew Team' }],
    creator: name,
    publisher: name,
    openGraph: {
      type: seo.openGraph.type,
      locale: toOpenGraphLocale(locale),
      url: getLocalizedNodeUrl(locale, '/'),
      siteName: seo.openGraph.siteName,
      title: copy.title,
      description: copy.description,
      images: [{ ...seo.openGraph.images.default, alt: copy.imageAlt }],
    },
    twitter: {
      card: seo.twitter.card,
      creator: seo.twitter.creator,
      site: seo.twitter.site,
    },
    icons: {
      // Flat vector marks (2.5 KB each). The previous favicon{1,2}.svg embedded
      // 1.2 MB raster textures that are invisible at favicon sizes.
      icon: [
        {
          url: '/logo/favicon-light.svg',
          type: 'image/svg+xml',
        },
        {
          url: '/logo/favicon-dark.svg',
          type: 'image/svg+xml',
          media: '(prefers-color-scheme: dark)',
        },
      ],
      apple: [
        {
          url: '/logo/favicon-light.svg',
          type: 'image/svg+xml',
        },
      ],
    },
    robots: {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        'max-video-preview': -1,
        'max-image-preview': 'large' as const,
        'max-snippet': -1,
      },
    },
    other: {
      'llms-txt': localizePath(locale, '/llms.txt'),
    },
  }
}

/**
 * Generate Organization JSON-LD schema
 */
export function getOrganizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    ...getOrganizationNode(),
  }
}

/**
 * Generate combined JSON-LD graph for home page. The app's
 * `SoftwareApplication` names every language it is served in.
 */
export function getHomePageJsonLd(t: SeoTranslator) {
  const { '@type': type, name, ...software } = siteConfig.jsonLd.software
  return {
    '@context': 'https://schema.org',
    '@graph': [
      getOrganizationNode(),
      {
        '@type': type,
        name,
        description: t('jsonLd.softwareDescription'),
        ...software,
        ...(isMultilingual()
          ? { inLanguage: ENABLED_LOCALES.map(toContentLanguage) }
          : {}),
      },
      {
        ...siteConfig.jsonLd.website,
        '@id': getWebsiteId(),
        url: getSiteUrl(),
        publisher: { '@id': getOrganizationId() },
      },
    ],
  }
}

/**
 * Generate FAQ JSON-LD schema from FAQ items, already in the page's
 * language.
 */
export function getFaqJsonLd(
  faqItems: Array<{ question: string; answer: string | Array<string> }>,
  locale: Locale
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: Array.isArray(item.answer) ? item.answer.join(' ') : item.answer,
      },
    })),
    ...getInLanguage(locale),
  }
}

// =============================================================================
// PROGRAMMATIC SEO JSON-LD GENERATORS
// =============================================================================

/**
 * Generate Product Comparison JSON-LD for comparison pages. The competitor's
 * name and description come from the comparison content.
 */
export function getComparisonJsonLd(
  t: SeoTranslator,
  locale: Locale,
  comparison: Readonly<{
    slug: string
    competitorName: string
    description: string
    /** The page's own title, so the structured name matches the `<title>`. */
    title?: string
    /** The day its claims were last checked (ISO date). */
    reviewedAt?: string
    /** The other product's domain, without the scheme. */
    competitorUrl?: string
  }>
) {
  const baseUrl = siteConfig.url

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name:
      comparison.title ??
      t('jsonLd.comparison.name', { competitor: comparison.competitorName }),
    description: comparison.description,
    ...(comparison.reviewedAt ? { dateModified: comparison.reviewedAt } : {}),
    // The slug, not the display name: Kit's page lives at brew-vs-convertkit.
    url: getLocalizedUrl(locale, `/compare/${comparison.slug}`),
    ...getInLanguage(locale),
    mainEntity: {
      '@type': 'ItemList',
      name: t('jsonLd.comparison.listName'),
      numberOfItems: 2,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'SoftwareApplication',
            name: 'Brew',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            description: t('pages.home.description'),
            url: baseUrl,
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'SoftwareApplication',
            name: comparison.competitorName,
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            ...(comparison.competitorUrl
              ? { url: `https://${comparison.competitorUrl}` }
              : {}),
          },
        },
      ],
    },
  }
}
